// @ts-nocheck
/**
 * Unified statements agent page: one session that maps an income statement
 * AND a balance sheet back-to-back (the backend chains the two existing
 * agents server-side; see agent/unified.py), then unlocks the cross-statement
 * deliverable — working capital / CCC metrics, combined insights, and the
 * CCC workbook export — on a single mega-card.
 *
 * Flow: two upload slots (P&L + BS; the same workbook can serve both), a
 * sheet pick per slot when needed, one Run click. The page then watches the
 * two child streams in sequence — phase 2's stream idles until its run
 * starts, so attaching early is safe. Restore/refresh loads the parent
 * snapshot (children's view_states) and reattaches to whichever child is
 * still active. Structure mirrors Agent.tsx deliberately.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Layers,
  FileSpreadsheet,
  X,
  RotateCcw,
  Sparkles,
  Upload,
  ChevronDown,
  ChevronRight,
  CheckCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  createAgentSession,
  uploadAgentFile,
  startUnifiedRun,
  getUnifiedSession,
  watchAgentSession,
} from '../../utils/agentApi';
import ToolCallBlock from './ToolCallBlock';
import FinalMappingCardUnified from './FinalMappingCardUnified';
import './Agent.css';
import './UnifiedAgent.css';

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const ROLE_LABELS = {
  pnl: 'Income statement',
  bs: 'Balance sheet',
};

const PHASE_ORDER = ['pnl', 'bs'];

const UnifiedAgent = ({ initialSessionId, onSessionIdChange, onSessionsRefresh }) => {
  const [sessionId, setSessionId] = useState(initialSessionId || null);
  // Per-slot upload state: { file: {file_id, filename, sheets}, sheetName }.
  const [slots, setSlots] = useState({ pnl: null, bs: null });
  const [chatItems, setChatItems] = useState([]);
  // Captured mapping payloads per statement, fed to the mega-card.
  const [mappings, setMappings] = useState({ pnl: null, bs: null });
  const [childIds, setChildIds] = useState({ pnl: null, bs: null });
  const [phase, setPhase] = useState(initialSessionId ? 'loading' : 'idle'); // idle | uploading | running | done | error | loading
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const restoredSessionRef = useRef(null);
  const onSessionsRefreshRef = useRef(onSessionsRefresh);
  useEffect(() => {
    onSessionsRefreshRef.current = onSessionsRefresh;
  }, [onSessionsRefresh]);
  const runActivityNotifiedRef = useRef(false);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setSessionId(null);
    setSlots({ pnl: null, bs: null });
    setChatItems([]);
    setMappings({ pnl: null, bs: null });
    setChildIds({ pnl: null, bs: null });
    setPhase('idle');
    setError(null);
    restoredSessionRef.current = null;
    onSessionIdChange?.(null);
  }, [onSessionIdChange]);

  const handleMappingUpdated = useCallback((role, updatedMapping) => {
    setMappings((prev) => ({ ...prev, [role]: updatedMapping }));
  }, []);

  // ---- upload slots ---------------------------------------------------

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const { session_id } = await createAgentSession();
    setSessionId(session_id);
    restoredSessionRef.current = session_id;
    onSessionIdChange?.(session_id);
    return session_id;
  }, [sessionId, onSessionIdChange]);

  const uploadToSlot = useCallback(
    async (role, file) => {
      setError(null);
      setPhase('uploading');
      try {
        const sid = await ensureSession();
        const uploaded = await uploadAgentFile(sid, file, role);
        const sheets = uploaded.sheets || [];
        setSlots((prev) => ({
          ...prev,
          [role]: {
            file: uploaded,
            sheetName: sheets.length === 1 ? sheets[0] : null,
          },
        }));
        setPhase('idle');
      } catch (err) {
        setError(err.message || String(err));
        setPhase('idle');
      }
    },
    [ensureSession],
  );

  // Reuse the other slot's uploaded workbook (same file_id, different sheet)
  // instead of uploading the same bytes twice.
  const useSameWorkbook = useCallback((role) => {
    setSlots((prev) => {
      const other = prev[role === 'pnl' ? 'bs' : 'pnl'];
      if (!other?.file) return prev;
      const sheets = other.file.sheets || [];
      // Prefer the first sheet the other slot did NOT take.
      const taken = other.sheetName;
      const candidate = sheets.find((s) => s !== taken) ?? null;
      return {
        ...prev,
        [role]: { file: other.file, sheetName: sheets.length === 1 ? sheets[0] : candidate },
      };
    });
  }, []);

  const setSlotSheet = useCallback((role, sheetName) => {
    setSlots((prev) =>
      prev[role] ? { ...prev, [role]: { ...prev[role], sheetName } } : prev,
    );
  }, []);

  const clearSlot = useCallback((role) => {
    setSlots((prev) => ({ ...prev, [role]: null }));
  }, []);

  // ---- run + watch -----------------------------------------------------

  const watchPhases = useCallback(
    async (phases, signal, resumeOffsets = {}) => {
      for (const ph of phases) {
        const role = ph.statement_type;
        setChildIds((prev) => ({ ...prev, [role]: ph.session_id }));
        setChatItems((prev) => {
          // One marker per phase; skip if the restore already added it.
          if (prev.some((it) => it.type === 'phase_marker' && it.role === role)) {
            return prev;
          }
          return [
            ...prev,
            { id: newId(), type: 'phase_marker', role },
          ];
        });
        await watchAgentSession(
          ph.session_id,
          resumeOffsets[role] || '0',
          (event, _eventId) => {
            if (!runActivityNotifiedRef.current) {
              runActivityNotifiedRef.current = true;
              onSessionsRefreshRef.current?.();
            }
            handleUnifiedEvent(event, role, setChatItems, setMappings);
          },
          signal,
        );
        if (signal.aborted) return;
      }
    },
    [],
  );

  // Reconcile with the server after the streams close: pick up final child
  // statuses + any mapping the stream missed (fallbacks live server-side).
  const finalizeFromSnapshot = useCallback(async (sid) => {
    try {
      const snap = await getUnifiedSession(sid);
      const nextMappings = {};
      const nextChildIds = {};
      for (const role of PHASE_ORDER) {
        const child = snap.children?.[role];
        nextChildIds[role] = snap.pair?.[role]?.child_session_id || null;
        if (!child) continue;
        const items = child.view_state?.chat_items || [];
        const found = items.find((it) => it?.type === 'mapping_result');
        nextMappings[role] = found?.mapping || child.final_mapping || null;
      }
      setChildIds((prev) => ({ ...prev, ...nextChildIds }));
      setMappings((prev) => ({
        pnl: nextMappings.pnl || prev.pnl,
        bs: nextMappings.bs || prev.bs,
      }));
      return snap;
    } catch {
      return null;
    }
  }, []);

  const canRun =
    slots.pnl?.file &&
    slots.pnl?.sheetName &&
    slots.bs?.file &&
    slots.bs?.sheetName &&
    !(
      slots.pnl.file.file_id === slots.bs.file.file_id &&
      slots.pnl.sheetName === slots.bs.sheetName
    );

  const runUnified = useCallback(async () => {
    if (!canRun || !sessionId) return;
    setError(null);
    setPhase('running');
    setChatItems([]);
    setMappings({ pnl: null, bs: null });
    runActivityNotifiedRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const result = await startUnifiedRun(sessionId, {
        pnl: { file_id: slots.pnl.file.file_id, sheet_name: slots.pnl.sheetName },
        bs: { file_id: slots.bs.file.file_id, sheet_name: slots.bs.sheetName },
      });
      // A 409 (chain already live in another tab) resolves with the same
      // phases — both tabs converge on the same child streams.
      await watchPhases(result.phases || [], ac.signal);
      const snap = await finalizeFromSnapshot(sessionId);
      if (snap?.status === 'failed') {
        setError('The run did not complete for both statements. The finished statement is still usable below.');
      }
      setPhase('done');
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || String(err));
        setPhase('error');
      } else {
        setPhase('done');
      }
    } finally {
      abortRef.current = null;
      setChatItems((prev) =>
        prev.map((it) =>
          it.type === 'assistant' && it.isStreaming
            ? { ...it, isStreaming: false }
            : it.type === 'tool_group' && it.isRunning
              ? { ...it, isRunning: false }
              : it,
        ),
      );
    }
  }, [canRun, sessionId, slots, watchPhases, finalizeFromSnapshot]);

  // ---- restore ----------------------------------------------------------

  useEffect(() => {
    if (!initialSessionId) {
      if (restoredSessionRef.current) reset();
      return;
    }
    if (restoredSessionRef.current === initialSessionId) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setError(null);
    setPhase('loading');
    setChatItems([]);
    setMappings({ pnl: null, bs: null });

    (async () => {
      try {
        const snap = await getUnifiedSession(initialSessionId);
        if (ac.signal.aborted) return;
        restoredSessionRef.current = initialSessionId;
        setSessionId(initialSessionId);

        // Rebuild the slot chips from the pairing (read-only on restore).
        const refsById = {};
        for (const ref of snap.file_refs || []) {
          if (ref?.file_id) refsById[ref.file_id] = ref;
        }
        const restoredSlots = {};
        for (const role of PHASE_ORDER) {
          const slot = snap.pair?.[role];
          const ref = slot ? refsById[slot.file_id] : null;
          restoredSlots[role] = slot
            ? {
                file: {
                  file_id: slot.file_id,
                  filename: ref?.filename || 'workbook.xlsx',
                  sheets: ref?.sheets || [],
                },
                sheetName: slot.sheet_name,
              }
            : null;
        }
        setSlots(restoredSlots);

        // Merge the children's persisted timelines with phase markers;
        // mapping_result items are captured, not rendered inline.
        const items = [];
        const restoredMappings = { pnl: null, bs: null };
        const restoredChildIds = { pnl: null, bs: null };
        const activePhases = [];
        const resumeOffsets = {};
        for (const role of PHASE_ORDER) {
          const child = snap.children?.[role];
          restoredChildIds[role] =
            snap.pair?.[role]?.child_session_id || null;
          if (!child) continue;
          const childItems = child.view_state?.chat_items || [];
          if (childItems.length > 0 || child.status === 'active') {
            items.push({ id: newId(), type: 'phase_marker', role });
          }
          for (const it of childItems) {
            if (it?.type === 'mapping_result') {
              restoredMappings[role] = it.mapping;
              items.push({ id: newId(), type: 'phase_done', role });
            } else {
              items.push({ ...it, id: it.id || newId() });
            }
          }
          if (!restoredMappings[role] && child.final_mapping) {
            restoredMappings[role] = child.final_mapping;
          }
          if (child.status === 'active') {
            activePhases.push({
              statement_type: role,
              session_id: child.session_id,
            });
            resumeOffsets[role] =
              child.view_state?._last_event_id || child.last_event_id || '0';
          }
        }
        setChatItems(items);
        setMappings(restoredMappings);
        setChildIds(restoredChildIds);

        if (snap.status === 'active') {
          setPhase('running');
          // Also queue the not-yet-started phase so the watcher rolls
          // straight into it when the chain gets there.
          const phasesToWatch = [...activePhases];
          for (const role of PHASE_ORDER) {
            const child = snap.children?.[role];
            const already = phasesToWatch.some(
              (p) => p.statement_type === role,
            );
            const done = child && child.status !== 'active';
            const cid = snap.pair?.[role]?.child_session_id;
            if (!already && !done && cid) {
              phasesToWatch.push({ statement_type: role, session_id: cid });
            }
          }
          try {
            await watchPhases(phasesToWatch, ac.signal, resumeOffsets);
            if (!ac.signal.aborted) {
              await finalizeFromSnapshot(initialSessionId);
              setPhase('done');
            }
          } catch (err) {
            if (ac.signal.aborted || err.name === 'AbortError') return;
            setError(err.message || String(err));
            setPhase('error');
          }
        } else {
          setPhase('done');
        }
      } catch (err) {
        if (ac.signal.aborted) return;
        setError(err?.message || String(err));
        setPhase('error');
      }
    })();

    return () => {
      ac.abort();
    };
    // Pinned to initialSessionId only — see Agent.tsx for the rationale
    // (parent re-renders every 5s from the sidebar poll; identity churn on
    // the callbacks would abort in-flight restores).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const showSetup = phase === 'idle' || phase === 'uploading';

  return (
    <div className="agent-root">
      <div className="agent-header">
        <div className="agent-header-inner">
          <div className="agent-header-title">
            <Layers size={18} />
            <span>Full Statements</span>
          </div>
          <div className="agent-header-actions">
            {!showSetup && (
              <div className="agent-file-chips">
                {PHASE_ORDER.map((role) => {
                  const s = slots[role];
                  if (!s?.file) return null;
                  return (
                    <span
                      key={role}
                      className="agent-file-chip"
                      title={`${ROLE_LABELS[role]}: ${s.file.filename}${
                        s.sheetName ? ` · ${s.sheetName}` : ''
                      }`}
                    >
                      <FileSpreadsheet size={12} />
                      <span className="agent-file-chip-name">
                        {ROLE_LABELS[role]}
                        <span className="agent-file-chip-divider"> · </span>
                        {s.sheetName || s.file.filename}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
            {!showSetup && (
              <button
                className="agent-newchat-btn"
                onClick={reset}
                disabled={phase === 'running'}
                title="Map another pair of statements"
              >
                <RotateCcw size={12} style={{ marginRight: 4 }} />
                Map another
              </button>
            )}
          </div>
        </div>
      </div>

      {showSetup ? (
        <SetupView
          slots={slots}
          uploading={phase === 'uploading'}
          onUpload={uploadToSlot}
          onSheet={setSlotSheet}
          onClear={clearSlot}
          onUseSame={useSameWorkbook}
          canRun={!!canRun}
          onRun={runUnified}
        />
      ) : (
        <UnifiedRunView
          phase={phase}
          chatItems={chatItems}
          mappings={mappings}
          childIds={childIds}
          parentId={sessionId}
          onMappingUpdated={handleMappingUpdated}
        />
      )}

      {error && (
        <div className="agent-error-banner">
          <X size={14} />
          {error}
          <button onClick={() => setError(null)} className="agent-error-dismiss">
            dismiss
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Setup view — the two statement slots + Run
// ---------------------------------------------------------------------------

const SlotDropzone = ({ role, slot, uploading, onUpload, onSheet, onClear, onUseSame, otherSlot }) => {
  const onDrop = useCallback(
    (accepted) => {
      if (accepted.length) onUpload(role, accepted[0]);
    },
    [role, onUpload],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: uploading,
  });

  const sheets = slot?.file?.sheets || [];
  const otherHasMultiSheet = (otherSlot?.file?.sheets || []).length > 1;

  return (
    <div className="ua-slot">
      <div className="ua-slot-title">{ROLE_LABELS[role]}</div>
      {!slot?.file ? (
        <>
          <div
            {...getRootProps()}
            className={`ua-slot-drop ${isDragActive ? 'drag-active' : ''}`}
          >
            <input {...getInputProps()} />
            <Upload size={18} />
            <span>Drop the {ROLE_LABELS[role].toLowerCase()} here</span>
          </div>
          {otherHasMultiSheet && (
            <button
              type="button"
              className="ua-slot-samefile"
              onClick={() => onUseSame(role)}
            >
              Use the same workbook, different sheet
            </button>
          )}
        </>
      ) : (
        <div className="ua-slot-filled">
          <div className="ua-slot-file">
            <FileSpreadsheet size={14} />
            <span className="ua-slot-filename" title={slot.file.filename}>
              {slot.file.filename}
            </span>
            <button
              type="button"
              className="ua-slot-clear"
              onClick={() => onClear(role)}
              title="Remove"
            >
              <X size={12} />
            </button>
          </div>
          {sheets.length > 1 ? (
            <select
              className="ua-slot-sheet"
              value={slot.sheetName || ''}
              onChange={(e) => onSheet(role, e.target.value)}
            >
              <option value="" disabled>
                Pick the sheet…
              </option>
              {sheets.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : (
            <div className="ua-slot-sheetname">{slot.sheetName}</div>
          )}
        </div>
      )}
    </div>
  );
};

const SetupView = ({ slots, uploading, onUpload, onSheet, onClear, onUseSame, canRun, onRun }) => (
  <div className="agent-empty ua-setup">
    <div className="ua-setup-inner">
      <h2 className="agent-empty-title">Map a full set of statements</h2>
      <p className="agent-empty-sub">
        Drop the income statement and the balance sheet (one workbook or two).
        The agent maps them one after the other, then unlocks the working
        capital cycle, cross-statement insights, and the CCC workbook.
      </p>
      <div className="ua-slots">
        <SlotDropzone
          role="pnl"
          slot={slots.pnl}
          otherSlot={slots.bs}
          uploading={uploading}
          onUpload={onUpload}
          onSheet={onSheet}
          onClear={onClear}
          onUseSame={onUseSame}
        />
        <SlotDropzone
          role="bs"
          slot={slots.bs}
          otherSlot={slots.pnl}
          uploading={uploading}
          onUpload={onUpload}
          onSheet={onSheet}
          onClear={onClear}
          onUseSame={onUseSame}
        />
      </div>
      {slots.pnl?.file &&
        slots.bs?.file &&
        slots.pnl.file.file_id === slots.bs.file.file_id &&
        slots.pnl.sheetName &&
        slots.pnl.sheetName === slots.bs.sheetName && (
          <p className="ua-setup-warn">
            Both slots point at the same sheet. Pick a different sheet for one
            of them.
          </p>
        )}
      <button
        type="button"
        className="ua-run-btn"
        onClick={onRun}
        disabled={!canRun || uploading}
      >
        <Sparkles size={14} />
        {uploading ? 'Uploading…' : 'Map both statements'}
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Run view — merged two-phase timeline + the mega-card
// ---------------------------------------------------------------------------

const THINKING_PHRASES = {
  get_sheet_structure: 'Studying the statement…',
  find_net_income: 'Planning the section breakdown…',
  find_balance_anchors: 'Finding the balance anchors…',
  classify_section: 'Sorting the next section…',
  classify_bs: 'Classifying the balance sheet…',
  read_rows: 'Reading the detail…',
  detect_totals: 'Checking the subtotals…',
  verify_net_profit: 'Reconciling the numbers…',
  check_balance: 'Checking the balance…',
  run_python: 'Crunching the numbers…',
};

const thinkingPhrase = (chatItems) => {
  for (let i = chatItems.length - 1; i >= 0; i--) {
    const it = chatItems[i];
    if (it?.type === 'tool_group' && it.calls?.length) {
      const lastCall = it.calls[it.calls.length - 1];
      return THINKING_PHRASES[lastCall?.toolName] || 'Working through the numbers…';
    }
  }
  return 'Getting oriented…';
};

const ThinkingIndicator = ({ label }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div
      className="agent-thinking is-breathing"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -4 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="agent-thinking-orb" aria-hidden="true">
        <span className="agent-thinking-orb-ripple" />
        <span className="agent-thinking-orb-core" />
      </span>
      <span className="agent-thinking-name">Thinking</span>
      <span className="agent-thinking-text">· {label}</span>
      {elapsed >= 1 && <span className="agent-thinking-elapsed">{elapsed}s</span>}
    </motion.div>
  );
};

const summarizeThought = (text, max = 90) => {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const m = cleaned.match(/^[^.!?]+[.!?](?=\s|$)/);
  const first = m ? m[0] : cleaned;
  if (first.length <= max) {
    return first + (cleaned.length > first.length ? '…' : '');
  }
  const cut = first.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
};

const ThoughtTab = ({ text, active, phrase }) => {
  const cleaned = text.replace(/<mapping>[\s\S]*?<\/mapping>/g, '').trim();
  const [expanded, setExpanded] = useState(false);
  if (!cleaned) return null;
  const label = active ? phrase : summarizeThought(cleaned);
  return (
    <div className="agent-thought">
      <button
        type="button"
        className={`agent-thought-header ${active ? 'is-breathing' : ''}`}
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="agent-thinking-orb" aria-hidden="true">
          <span className="agent-thinking-orb-ripple" />
          <span className="agent-thinking-orb-core" />
        </span>
        <span className="agent-thinking-name">Thinking</span>
        <span className="agent-thinking-text">· {label}</span>
        {expanded ? (
          <ChevronDown size={12} className="agent-thought-chevron" />
        ) : (
          <ChevronRight size={12} className="agent-thought-chevron" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="agent-thought-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="agent-thought-prose">{cleaned}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const UnifiedRunView = ({ phase, chatItems, mappings, childIds, parentId, onMappingUpdated }) => {
  const scrollRef = useRef(null);
  const lastItem = chatItems[chatItems.length - 1];
  const lastIndex = chatItems.length - 1;
  const phrase = thinkingPhrase(chatItems);
  const showStandalone =
    phase === 'running' &&
    (chatItems.length === 0 ||
      lastItem?.type === 'phase_marker' ||
      lastItem?.type === 'phase_done' ||
      (lastItem?.type === 'tool_group' && !lastItem.isRunning));

  const bothMapped = !!(mappings.pnl && mappings.bs);
  // A finished run renders whatever survived — a failed child no longer
  // hides the other statement. Mid-run the card still waits for both phases
  // so a still-running second phase isn't presented as failed.
  const showMappingCard =
    bothMapped || (phase === 'done' && !!(mappings.pnl || mappings.bs));

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatItems, showStandalone, showMappingCard]);

  return (
    <div className="agent-run">
      <div className="agent-run-stream" ref={scrollRef}>
        {phase === 'loading' && (
          <div className="agent-run-pending">
            <Sparkles size={14} className="spin-slow" />
            <span>Loading session…</span>
          </div>
        )}

        {chatItems.map((item, idx) => {
          if (item.type === 'phase_marker') {
            return (
              <div key={item.id} className="ua-phase-marker">
                <span className="ua-phase-line" aria-hidden />
                <span className="ua-phase-label">
                  {ROLE_LABELS[item.role]} mapping
                </span>
                <span className="ua-phase-line" aria-hidden />
              </div>
            );
          }
          if (item.type === 'phase_done') {
            return (
              <div key={item.id} className="ua-phase-done">
                <CheckCircle size={13} />
                <span>{ROLE_LABELS[item.role]} mapped</span>
              </div>
            );
          }
          if (item.type === 'tool_group') {
            return (
              <ToolCallBlock
                key={item.id}
                calls={item.calls}
                isRunning={item.isRunning}
              />
            );
          }
          if (item.type === 'assistant') {
            const text = (item.text || '').trim();
            if (!text) return null;
            const active = phase === 'running' && idx === lastIndex;
            return (
              <ThoughtTab key={item.id} text={text} active={active} phrase={phrase} />
            );
          }
          return null;
        })}

        <AnimatePresence>
          {showStandalone && (
            <ThinkingIndicator key="thinking-indicator" label={phrase} />
          )}
        </AnimatePresence>

        {showMappingCard && (
          <FinalMappingCardUnified
            parentId={parentId}
            pnl={{ mapping: mappings.pnl, sessionId: childIds.pnl }}
            bs={{ mapping: mappings.bs, sessionId: childIds.bs }}
            onPnlMappingUpdated={(m) => onMappingUpdated('pnl', m)}
            onBsMappingUpdated={(m) => onMappingUpdated('bs', m)}
            runComplete={phase === 'done'}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SSE event → merged-timeline reducer (phase-aware Agent.tsx reducer:
// mapping_complete is captured into `mappings` and rendered as a compact
// phase_done pill; the mega-card renders once BOTH statements are in).
// ---------------------------------------------------------------------------

function handleUnifiedEvent(event, role, setChatItems, setMappings) {
  const { type, data } = event;

  if (type === 'text_delta') {
    const chunk = data?.text || '';
    if (!chunk) return;
    setChatItems((prev) => {
      const last = prev[prev.length - 1];
      if (last?.type === 'assistant' && last.isStreaming) {
        return [...prev.slice(0, -1), { ...last, text: last.text + chunk }];
      }
      return [
        ...prev,
        { id: newId(), type: 'assistant', text: chunk, isStreaming: true, role },
      ];
    });
    return;
  }

  if (type === 'tool_start') {
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it,
      );
      const last = closed[closed.length - 1];
      const entry = {
        toolUseId: data.tool_use_id,
        toolName: data.tool_name,
        input: data.input,
        status: 'running',
      };
      if (last?.type === 'tool_group' && last.isRunning) {
        return [...closed.slice(0, -1), { ...last, calls: [...last.calls, entry] }];
      }
      return [
        ...closed,
        { id: newId(), type: 'tool_group', calls: [entry], isRunning: true, role },
      ];
    });
    return;
  }

  if (type === 'tool_result') {
    setChatItems((prev) =>
      prev.map((it) => {
        if (it.type !== 'tool_group') return it;
        let touched = false;
        const calls = it.calls.map((c) => {
          if (c.toolUseId !== data.tool_use_id) return c;
          touched = true;
          return {
            ...c,
            result: data.result,
            isError: !!data.is_error,
            status: data.is_error ? 'error' : 'done',
          };
        });
        if (!touched) return it;
        const stillRunning = calls.some((c) => c.status === 'running');
        return { ...it, calls, isRunning: stillRunning };
      }),
    );
    return;
  }

  if (type === 'mapping_complete') {
    setMappings((prev) => ({ ...prev, [role]: data }));
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      );
      return [...closed, { id: newId(), type: 'phase_done', role }];
    });
    return;
  }

  if (type === 'done') {
    setChatItems((prev) =>
      prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      ),
    );
    return;
  }

  if (type === 'error') {
    setChatItems((prev) => [
      ...prev,
      {
        id: newId(),
        type: 'assistant',
        text: `⚠ ${data?.message || 'error'}`,
        isStreaming: false,
        role,
      },
    ]);
  }
}

export default UnifiedAgent;
